import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getInviteByToken } from "@/lib/api/invites";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Loader2, Shield, Lock, Server, Users, ArrowLeft, CheckCircle2, Building2, UserPlus } from "lucide-react";
import quentariLogo from "@/assets/quentari-logo.svg";

// Reuse exact Auth background for visual consistency
function InviteBackground({ stageBg, stageGrid1, stageGrid2 }: { stageBg: boolean; stageGrid1: boolean; stageGrid2: boolean }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 transition-opacity duration-1000"
        style={{
          opacity: stageBg ? 1 : 0,
          background: `
            radial-gradient(ellipse 80% 50% at 50% 0%, hsl(var(--primary) / 0.04) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 0% 50%, hsl(var(--primary) / 0.03) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 100% 50%, hsl(var(--primary) / 0.03) 0%, transparent 50%)
          `,
        }}
      />
      <div
        className="absolute inset-0 animate-[subtle-pulse-bg_6s_ease-in-out_infinite]"
        style={{
          background: 'radial-gradient(circle at 50% 50%, hsl(var(--primary) / 0.06) 0%, transparent 60%)',
        }}
      />
      <div
        className="absolute inset-[-20%] transition-all duration-1000"
        style={{
          opacity: stageGrid1 ? 1 : 0,
          transform: stageGrid1 ? 'scale(1)' : 'scale(0.98)',
          backgroundImage: `
            linear-gradient(hsl(var(--primary) / 0.05) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary) / 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 40%, black 10%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 40%, black 10%, transparent 70%)',
        }}
      />
      <div
        className="absolute inset-[-10%] transition-opacity duration-1000"
        style={{
          opacity: stageGrid2 ? 1 : 0,
          backgroundImage: `
            linear-gradient(hsl(var(--primary) / 0.02) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary) / 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '15px 15px',
          maskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 5%, transparent 60%)',
          WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 50%, black 5%, transparent 60%)',
        }}
      />
      <div
        className="absolute top-1/4 left-8 w-px h-24 bg-gradient-to-b from-transparent via-primary/15 to-transparent hidden md:block animate-[float_8s_ease-in-out_infinite,float-vary-opacity_6s_ease-in-out_infinite]"
      />
      <div
        className="absolute top-1/3 right-8 w-px h-20 bg-gradient-to-b from-transparent via-primary/10 to-transparent hidden md:block animate-[float_10s_ease-in-out_infinite,float-vary-opacity_7s_ease-in-out_infinite]"
        style={{ animationDelay: '2s' }}
      />
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse 100% 100% at 50% 50%, transparent 20%, hsl(var(--background)) 100%)`,
        }}
      />
    </div>
  );
}

// Invite visual – left side illustration
function InviteVisual({ stage }: { stage: boolean }) {
  return (
    <div
      className={`hidden lg:flex flex-1 flex-col items-center justify-center transition-all duration-1000 ${
        stage ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
      }`}
      style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
    >
      <div className="text-center mb-10">
        <h2
          className="text-2xl font-serif font-semibold text-foreground mb-2"
          style={{
            animation: stage ? 'blur-deblur 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards' : 'none',
            opacity: 0,
          }}
        >
          Dein Team erwartet dich.
        </h2>
        <p
          className="text-sm text-muted-foreground"
          style={{
            animation: stage ? 'blur-deblur 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.6s forwards' : 'none',
            opacity: 0,
          }}
        >
          Gemeinsam bessere Entscheidungen treffen.
        </p>
      </div>

      {/* Concentric rings visual */}
      <div className="relative w-64 h-64">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute inset-0 rounded-full border border-primary/10"
            style={{
              animation: stage
                ? `scale-in 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${0.4 + i * 0.15}s forwards, subtle-pulse-bg ${4 + i}s ease-in-out infinite`
                : 'none',
              opacity: 0,
              transform: 'scale(0.8)',
              inset: `${i * 28}px`,
            }}
          />
        ))}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            animation: stage ? 'scale-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.8s forwards' : 'none',
            opacity: 0,
          }}
        >
          <div className="w-20 h-20 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shadow-lg shadow-primary/10">
            <Users className="h-9 w-9 text-primary" />
          </div>
        </div>
        {/* Floating member dots */}
        {[
          { top: '15%', left: '10%', delay: '0.9s', size: 'w-3 h-3' },
          { top: '8%', right: '20%', delay: '1s', size: 'w-2.5 h-2.5' },
          { bottom: '20%', left: '5%', delay: '1.1s', size: 'w-2 h-2' },
          { bottom: '10%', right: '15%', delay: '1.2s', size: 'w-3 h-3' },
        ].map((pos, i) => (
          <div
            key={i}
            className={`absolute ${pos.size} rounded-full bg-primary/20 border border-primary/30`}
            style={{
              top: pos.top,
              left: pos.left,
              right: pos.right,
              bottom: pos.bottom,
              animation: stage
                ? `scale-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${pos.delay} forwards, float ${6 + i}s ease-in-out infinite`
                : 'none',
              opacity: 0,
            }}
          />
        ))}
      </div>
    </div>
  );
}

const trustItems = [
  { icon: Shield, label: "DSGVO-konform" },
  { icon: Lock, label: "Verschlüsselte Daten" },
  { icon: Server, label: "EU-Hosting" },
];

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCorrectUser, setIsCorrectUser] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [showSignup, setShowSignup] = useState(false);
  const [signupData, setSignupData] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
  });

  // Animation stages – mirroring Auth.tsx orchestration
  const [stageBg, setStageBg] = useState(false);
  const [stageGrid1, setStageGrid1] = useState(false);
  const [stageGrid2, setStageGrid2] = useState(false);
  const [stageHeader, setStageHeader] = useState(false);
  const [stageHeadline, setStageHeadline] = useState(false);
  const [stageSubtext, setStageSubtext] = useState(false);
  const [stageCard, setStageCard] = useState(false);
  const [stageVisual, setStageVisual] = useState(false);
  const [stageTrust, setStageTrust] = useState([false, false, false]);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStageBg(true), 0),
      setTimeout(() => setStageGrid1(true), 150),
      setTimeout(() => setStageVisual(true), 200),
      setTimeout(() => setStageGrid2(true), 300),
      setTimeout(() => setStageHeader(true), 400),
      setTimeout(() => setStageHeadline(true), 500),
      setTimeout(() => setStageSubtext(true), 600),
      setTimeout(() => setStageCard(true), 700),
      setTimeout(() => setStageTrust([true, false, false]), 900),
      setTimeout(() => setStageTrust([true, true, false]), 1000),
      setTimeout(() => setStageTrust([true, true, true]), 1100),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    async function loadInvite() {
      if (!token) {
        setError("Ungültiger Einladungslink.");
        setLoading(false);
        return;
      }
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setIsLoggedIn(!!user);
        setCurrentUserEmail(user?.email || null);

        const inviteData = await getInviteByToken(token);

        if (new Date(inviteData.expires_at) < new Date()) {
          setError("Diese Einladung ist abgelaufen.");
          setLoading(false);
          return;
        }
        if (inviteData.accepted_at) {
          setError("Diese Einladung wurde bereits angenommen.");
          setLoading(false);
          return;
        }

        setInvite(inviteData);
        if (user && user.email?.toLowerCase() === inviteData.email.toLowerCase()) {
          setIsCorrectUser(true);
        }
      } catch {
        setError("Einladung nicht gefunden.");
      } finally {
        setLoading(false);
      }
    }
    loadInvite();
  }, [token]);

  const redirectAfterAccept = (data: any) => {
    setAccepted(true);
    // Brief success moment before redirect
    setTimeout(() => {
      navigate('/welcome', {
        replace: true,
        state: {
          workspaceId: invite.workspace_id,
          workspaceName: data.workspaceName || invite?.workspaces?.name,
          role: invite.role,
        },
      });
    }, 1200);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signupData.password !== signupData.confirmPassword) {
      toast({ title: "Passwörter stimmen nicht überein", variant: "destructive" });
      return;
    }
    if (signupData.password.length < 8) {
      toast({ title: "Passwort zu kurz", description: "Mindestens 8 Zeichen.", variant: "destructive" });
      return;
    }

    setAccepting(true);
    try {
      const { data, error } = await supabase.functions.invoke('accept-invite', {
        body: { token, password: signupData.password, firstName: signupData.firstName, lastName: signupData.lastName },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invite.email,
        password: signupData.password,
      });
      if (signInError) { navigate('/auth'); return; }

      redirectAfterAccept(data);
    } catch (err: unknown) {
      toast({ title: "Fehler bei der Registrierung", description: err instanceof Error ? err.message : 'Fehler', variant: "destructive" });
    } finally {
      setAccepting(false);
    }
  };

  const handleAccept = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setShowSignup(true); return; }
    if (user.email?.toLowerCase() !== invite?.email?.toLowerCase()) {
      toast({ title: "Falsche E-Mail-Adresse", description: "Sie sind mit einer anderen E-Mail angemeldet.", variant: "destructive" });
      return;
    }
    setAccepting(true);
    try {
      const { data, error } = await supabase.functions.invoke('accept-invite', {
        body: { token, existingUserId: user.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      redirectAfterAccept(data);
    } catch (err: unknown) {
      toast({ title: "Fehler", description: err instanceof Error ? err.message : 'Fehler', variant: "destructive" });
    } finally {
      setAccepting(false);
    }
  };

  const handleLogoutAndCreateAccount = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setIsCorrectUser(false);
    setCurrentUserEmail(null);
    setShowSignup(true);
  };

  const roleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Inhaber';
      case 'admin': return 'Administrator';
      default: return 'Mitglied';
    }
  };

  // ========== SUCCESS STATE ==========
  if (accepted) {
    return (
      <div className="min-h-screen bg-background flex flex-col relative">
        <InviteBackground stageBg={true} stageGrid1={true} stageGrid2={true} />
        <div className="flex-1 flex items-center justify-center relative z-10">
          <div className="text-center animate-scale-in">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-serif font-semibold text-foreground mb-2">Willkommen im Team!</h2>
            <p className="text-muted-foreground text-sm">Du wirst jetzt weitergeleitet…</p>
          </div>
        </div>
      </div>
    );
  }

  // ========== LOADING STATE ==========
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center relative">
        <InviteBackground stageBg={true} stageGrid1={false} stageGrid2={false} />
        <div className="relative z-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // ========== ERROR STATE ==========
  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col relative">
        <InviteBackground stageBg={true} stageGrid1={true} stageGrid2={false} />
        <header className="w-full border-b border-border/50 backdrop-blur-sm bg-background/80 relative z-10">
          <div className="container flex h-16 items-center max-w-7xl mx-auto px-4 md:px-6">
            <Link to="/" className="transition-opacity hover:opacity-80">
              <img src={quentariLogo} alt="Quentari" className="h-8 w-auto" />
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 relative z-10">
          <div className="text-center max-w-md animate-fade-in">
            <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-destructive/10 border border-destructive/20 flex items-center justify-center">
              <Shield className="h-7 w-7 text-destructive" />
            </div>
            <h2 className="text-xl font-serif font-semibold text-foreground mb-2">Einladung ungültig</h2>
            <p className="text-muted-foreground text-sm mb-6">{error}</p>
            <Button onClick={() => navigate('/')}>Zur Startseite</Button>
          </div>
        </main>
      </div>
    );
  }

  // ========== SIGNUP FORM ==========
  const signupContent = showSignup && !isLoggedIn;
  const workspaceName = invite?.workspaces?.name || 'Workspace';

  // ========== MAIN CONTENT (right side) ==========
  const renderContent = () => {
    if (signupContent) {
      return (
        <form onSubmit={handleSignup} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">E-Mail</Label>
            <Input id="email" type="email" value={invite?.email || ""} disabled className="h-11 bg-muted/50 border-border/60" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-sm font-medium">Vorname</Label>
              <Input id="firstName" type="text" value={signupData.firstName} onChange={(e) => setSignupData({ ...signupData, firstName: e.target.value })} required disabled={accepting} className="h-11 bg-background/50 border-border/60 auth-input-focus" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-sm font-medium">Nachname</Label>
              <Input id="lastName" type="text" value={signupData.lastName} onChange={(e) => setSignupData({ ...signupData, lastName: e.target.value })} required disabled={accepting} className="h-11 bg-background/50 border-border/60 auth-input-focus" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">Passwort</Label>
            <Input id="password" type="password" value={signupData.password} onChange={(e) => setSignupData({ ...signupData, password: e.target.value })} required disabled={accepting} minLength={8} className="h-11 bg-background/50 border-border/60 auth-input-focus" placeholder="Mindestens 8 Zeichen" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium">Passwort bestätigen</Label>
            <Input id="confirmPassword" type="password" value={signupData.confirmPassword} onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })} required disabled={accepting} minLength={8} className="h-11 bg-background/50 border-border/60 auth-input-focus" />
          </div>
          <Button type="submit" className="w-full h-12 text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 auth-shimmer-btn" disabled={accepting}>
            {accepting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Wird erstellt…</> : <><UserPlus className="mr-2 h-5 w-5" />Account erstellen & beitreten</>}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate('/auth')} disabled={accepting} className="w-full text-sm text-muted-foreground">
            Bereits ein Account? Anmelden
          </Button>
        </form>
      );
    }

    // Invite info + actions
    return (
      <div className="space-y-6">
        {/* Workspace card */}
        <div className="p-4 rounded-xl border border-border/50 bg-muted/20 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{workspaceName}</p>
              <p className="text-xs text-muted-foreground">Einladung als <span className="font-medium text-foreground">{roleLabel(invite?.role)}</span></p>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border/30">
            <span>Eingeladene E-Mail</span>
            <span className="font-medium text-foreground">{invite?.email}</span>
          </div>
        </div>

        {isLoggedIn && isCorrectUser ? (
          <Button className="w-full h-12 text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 auth-shimmer-btn" onClick={handleAccept} disabled={accepting}>
            {accepting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Wird angenommen…</> : <><Users className="mr-2 h-5 w-5" />Deinem Team beitreten</>}
          </Button>
        ) : isLoggedIn && !isCorrectUser ? (
          <div className="space-y-3">
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-xl text-sm">
              <p className="font-medium text-destructive">Falsche E-Mail-Adresse</p>
              <p className="text-muted-foreground mt-1 text-xs">
                Angemeldet als <span className="font-medium">{currentUserEmail}</span>, Einladung für <span className="font-medium">{invite?.email}</span>.
              </p>
            </div>
            <Button className="w-full h-11" onClick={handleLogoutAndCreateAccount}>
              Abmelden und neuen Account erstellen
            </Button>
            <Button variant="outline" className="w-full h-11" onClick={async () => { await supabase.auth.signOut(); navigate('/auth'); }}>
              Mit {invite?.email} anmelden
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Button className="w-full h-12 text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 auth-shimmer-btn" onClick={handleAccept} disabled={accepting}>
              <UserPlus className="mr-2 h-5 w-5" />Account erstellen & beitreten
            </Button>
            <Button variant="outline" onClick={() => navigate('/auth')} className="w-full h-11">
              Bereits ein Account? Anmelden
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      <InviteBackground stageBg={stageBg} stageGrid1={stageGrid1} stageGrid2={stageGrid2} />

      {/* Header */}
      <header
        className={`w-full border-b border-border/50 backdrop-blur-sm bg-background/80 relative z-10 transition-all duration-500 ${
          stageHeader ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        <div className="container flex h-16 items-center justify-between max-w-7xl mx-auto px-4 md:px-6">
          <Link to="/" className="transition-opacity hover:opacity-80">
            <img src={quentariLogo} alt="Quentari" className="h-8 w-auto" />
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zur Startseite
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 md:px-6 py-8 md:py-16 relative z-10">
        <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center gap-8 lg:gap-16">

          {/* Left side: Visual */}
          <InviteVisual stage={stageVisual} />

          {/* Right side: Content */}
          <div className="w-full max-w-sm">
            {/* Mobile visual hint */}
            <div className="lg:hidden mb-6 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 text-sm text-primary">
                <Users className="h-4 w-4" />
                Team-Einladung
              </div>
            </div>

            {/* Headline */}
            <div className="mb-8 text-center lg:text-left">
              <h1
                className="text-2xl sm:text-3xl md:text-4xl font-semibold font-serif text-foreground mb-2 relative"
                style={{
                  animation: stageHeadline ? 'blur-deblur 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'none',
                  opacity: stageHeadline ? undefined : 0,
                  filter: stageHeadline ? undefined : 'blur(8px)',
                  textShadow: `
                    0 1px 2px hsl(var(--foreground) / 0.04),
                    0 4px 8px hsl(var(--foreground) / 0.02),
                    0 8px 16px hsl(var(--primary) / 0.04)
                  `,
                }}
              >
                {signupContent ? 'Willkommen an Bord.' : 'Du wurdest eingeladen.'}
              </h1>
              <p
                className={`text-sm text-muted-foreground leading-relaxed transition-all duration-500 ${
                  stageSubtext ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
                style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
              >
                {signupContent
                  ? `Erstelle deinen Account für ${workspaceName}.`
                  : `${workspaceName} möchte gemeinsam mit dir Entscheidungen treffen.`}
              </p>
            </div>

            {/* Card with glow */}
            <div
              className={`relative transition-all duration-700 ${
                stageCard ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-[0.96] translate-y-4'
              }`}
              style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              <div
                className="absolute -inset-px rounded-2xl auth-glow-rotate opacity-60"
                style={{
                  background: 'linear-gradient(135deg, hsl(var(--primary) / 0.12) 0%, transparent 40%, hsl(var(--primary) / 0.08) 60%, transparent 100%)',
                  backgroundSize: '200% 200%',
                }}
              />
              <div className="relative bg-card/80 backdrop-blur-sm border border-border/60 rounded-2xl p-6 shadow-xl shadow-primary/5">
                {renderContent()}
              </div>
            </div>

            {/* Trust badges */}
            <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-xs text-muted-foreground/70">
              {trustItems.map((item, i) => (
                <span
                  key={i}
                  className={`flex items-center gap-1.5 transition-all duration-500 ${
                    stageTrust[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                  }`}
                  style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
                >
                  <item.icon className="h-3.5 w-3.5" />
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
